import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";

// 1. Move content structures to a configurations array
const SLIDES_DATA = [
  {
    id: "ai-readiness",
    title: (
      <>
        AI READINESS
        <br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-white">
          ASSESSMENT
        </span>
      </>
    ),
    subtitle: "Building the Foundation for Responsible AI Adoption",
    description: (
      <>
        Artificial Intelligence is rapidly transforming how organizations
        operate, innovate, and compete. However, many enterprises struggle to
        operationalize AI securely due to fragmented governance, data
        challenges, evolving compliance requirements, and unclear business
        alignment.
        <br />
        <br />
        Synthera Security’s AI Readiness Assessment helps organizations evaluate
        their preparedness to adopt and scale AI securely and responsibly.
        Through a strategic, outcome-driven approach, we assess AI maturity
        across governance, security, infrastructure, data, operations, and
        workforce readiness helping enterprises build a clear roadmap for
        successful AI adoption.
      </>
    ),
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780214429/Ai_readiness_pxphkb.jpg",
    imageAlt: "AI Readiness Assessment",
    linkTo: "/AIReadinessAssessment",
  },
  {
    id: "ai-pentesting",
    title: (
      <>
        AI PENETRATION TESTING
        <br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-white">
          AND RED TEAMING
        </span>
      </>
    ),
    subtitle: "Comprehensive Security for AI-Enabled Systems",
    description: (
      <>
        In an era where Artificial Intelligence (AI) and Large Language Models
        (LLMs) are increasingly integrated into critical business operations,
        traditional security testing falls short. Standard "AI red teaming"
        often narrowly focuses on provoking undesirable outputs from the model
        itself.
        <br />
        <br />
        Synthera Security, AI Penetration Testing Service redefines AI security
        assessments by adopting a holistic, ecosystem wide approach. We
        scrutinize every layer of your AI-enabled applications, from user inputs
        and data pipelines to the surrounding infrastructure and downstream
        business workflows.
      </>
    ),
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1779089479/penetartion_vgvtvs.png",
    imageAlt: "AI Red Teaming",
    linkTo: "/AiPenetrationTesting",
  },
  {
    id: "SecureAIEcosystem",
    title: (
      <>
        SECURE {""}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-white">
          THE AI TRANSFORMATION
        </span>
      </>
    ),
    subtitle: "Securing Generative AI and Autonomous Workflows",
    description: (
      <>
        Artificial Intelligence is rapidly reshaping enterprise operations
        through Generative AI, AI agents, autonomous workflows, and agentic AI
        systems. As AI adoption accelerates, traditional security models
        struggle to address risks introduced by AI interactions, third-party
        models, APIs, plugins, data exposure, and autonomous execution.
        <br />
        <br />
        Synthera Security’s AI Security Service helps organizations secure AI
        ecosystems through a strategic, risk-driven approach focused on
        protecting AI workflows, applications, agents, and connected systems. We
        help enterprises implement AI Guardrails, AI Gateways, MCP Security,
        governance controls, and policy enforcement to enable secure and
        compliant AI innovation.
      </>
    ),
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780319023/ChatGPT_Image_Jun_1_2026_05_03_52_PM_j0xkup.png",
    imageAlt: "Secure the AI",
    linkTo: "/SecureAIEcosystem",
  },
  {
    id: "data-security",
    title: (
      <>
        DATA
        <br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-white">
          SECURITY
        </span>
      </>
    ),
    subtitle: "Protecting Sensitive Data Across Modern Environments",
    description: (
      <>
        Data is one of the most valuable assets in the modern enterprise and one
        of the most targeted. As organizations adopt cloud platforms, SaaS
        applications, AI technologies, hybrid work models, and distributed
        environments, sensitive data increasingly moves beyond traditional
        security boundaries creating significant visibility, governance, and
        protection challenges.
        <br />
        <br />
        Synthera Security’s Data Security Service helps organizations discover,
        classify, govern, and protect sensitive data across users, endpoints,
        cloud platforms, SaaS applications, AI ecosystems, and hybrid
        environments. Through a strategic, risk-driven approach, we help
        enterprises strengthen data protection, reduce exposure risks, improve
        regulatory readiness, and maintain visibility and control over critical
        business data.
      </>
    ),
    image:
      "  https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780331593/ChatGPT_Image_Jun_1_2026_07_53_35_PM_1_aywfmy.png",
    imageAlt: "Data Security",
    linkTo: "/DataSecurity",
  },

  {
    id: "sase-transformation",
    title: (
      <>
        SASE
        <br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-white">
          Transformation
        </span>
      </>
    ),
    subtitle:
      "Modernising Network and Security for the Cloud-First, Hybrid Workforce",
    description: (
      <>
        Legacy network and security architectures are struggling to support
        today’s cloud-first, hybrid, and AI-driven environments. Synthera
        Security’s SASE Transformation Service helps organizations modernize
        connectivity and security through a strategic, outcome-driven approach
        built on SASE, Zero Trust, and cloud-native architectures.
        <br />
        <br />
        From strategy and roadmap development to migration and
        operationalization, we help enterprises simplify operations, strengthen
        security, and transition from fragmented legacy environments to
        scalable, resilient, and cloud-delivered security frameworks.
      </>
    ),
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780214428/sase_T_n01yp9.jpg",
    imageAlt: "SASE Transformation",
    linkTo: "/SASETransformation",
  },

  {
    id: "identity-security",
    title: (
      <>
        IDENTITY{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-white">
          SECURITY
        </span>
      </>
    ),
    subtitle: "Securing Human and Non-Human Identities",
    description: (
      <>
        Identity has become the new security perimeter as organizations adopt
        cloud platforms, SaaS applications, automation, AI, and hybrid work
        models. The rapid growth of both human and non-human identities
        increases complexity, visibility gaps, and security risks across the
        enterprise.
        <br />
        <br />
        Synthera Security’s Identity Security Service helps organizations
        strengthen governance, visibility, and protection across workforce
        identities, privileged accounts, machine identities, service accounts,
        APIs, workloads, and automated systems. We help enterprises modernize
        identity security, reduce attack surfaces, and enforce Zero Trust
        principles across the identity ecosystem.
      </>
    ),
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780214430/identity_jv4epy.jpg",
    imageAlt: "Identity Security",
    linkTo: "/IdentitySecurity",
  },

  {
    id: "browser-security",
    title: (
      <>
        BROWSER{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-white">
          SECURITY
        </span>
      </>
    ),
    subtitle: "Securing the Modern Browser-Driven Enterprise",
    description: (
      <>
        The web browser has become the primary workspace for today’s hybrid
        workforce serving as the gateway to SaaS applications, cloud platforms,
        corporate data, and AI-driven services. Traditional security controls
        often lack visibility and control at the browser layer, exposing
        organizations to phishing, data leakage, malicious extensions, shadow
        IT, and unmanaged device risks.
        <br />
        <br />
        Synthera Security’s Browser Security Service helps enterprises
        strengthen browser-layer protection, improve visibility, and enable
        secure access to business-critical applications, cloud services, and AI
        platforms across managed and unmanaged environments.
      </>
    ),
    image:
      "https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780214429/browser_s_ie8emz.jpg",
    imageAlt: "Browser Security",
    linkTo: "/BrowserSecurity",
  },
];

const DataSecuritySection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // 2. Set up automated interval slider loop
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % SLIDES_DATA.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, []);

  const currentSlide = SLIDES_DATA[currentIndex];

  return (
    <ScrollReveal>
      <section className="relative bg-[#ffffff] py-12 px-4 md:px-8 overflow-hidden">
        {/* Compact Explore Link */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black-10 text-[#165149] tracking-tight">
            Securing What's Next
          </h2>
        </div>
        <div className="relative w-full lg:w-[90%] xl:w-[85%] max-w-[1400px] mx-auto group">
          {/* Animated Neon Border Glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600 to-teal-900 rounded-[2.5rem] md:rounded-[3rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>

          <div className="relative bg-[#021f1b] rounded-[2rem] md:rounded-[2.5rem] overflow-hidden border border-white/10 shadow-2xl h-[750px] lg:h-[720px] flex flex-col justify-stretch">
            {/* AnimatePresence manages the transition gracefully between changing key attributes */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide.id}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className="flex flex-col lg:flex-row items-stretch w-full h-full flex-grow"
              >
                {/* Left Content */}
                <div className="flex-[1.2] p-8 md:p-12 lg:p-16 z-10 bg-[#021f1b] flex flex-col justify-center">
                  <div>
                    <h2 className="text-2xl md:text-4xl xl:text-5xl font-black text-white leading-[1.1] mb-3">
                      {currentSlide.title}
                    </h2>

                    <h4 className="text-emerald-400 text-sm md:text-base xl:text-lg font-bold mb-6 tracking-wide uppercase">
                      {currentSlide.subtitle}
                    </h4>

                    <p className="text-gray-400 text-xs md:text-sm xl:text-base leading-relaxed mb-10">
                      {currentSlide.description}
                    </p>

                    <Link
                      to={currentSlide.linkTo}
                      className="relative inline-flex items-center justify-center px-8 md:px-10 py-3 md:py-4 overflow-hidden font-bold text-white transition duration-300 ease-out border-2 border-emerald-500 rounded-full shadow-md group/btn w-fit"
                    >
                      <span className="absolute inset-0 flex items-center justify-center w-full h-full text-[#021f1b] duration-300 -translate-x-full bg-emerald-500 group-hover/btn:translate-x-0 ease">
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          ></path>
                        </svg>
                      </span>
                      <span className="absolute flex items-center justify-center w-full h-full text-emerald-400 transition-all duration-300 transform group-hover/btn:translate-x-full ease font-black uppercase tracking-widest text-xs md:text-sm">
                        Learn More
                      </span>
                      <span className="relative invisible">Learn More</span>
                    </Link>
                  </div>
                </div>

                {/* Right Content / Image Side */}
                <div className="flex-1 relative min-h-[300px] lg:min-h-full overflow-hidden bg-[#011412]">
                  {/* Slant Divider */}

                  <motion.div
                    className="h-full w-full"
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.8 }}
                  >
                    <img
                      src={currentSlide.image}
                      alt={currentSlide.imageAlt}
                      className="w-full h-full object-cover object-center absolute inset-0"
                    />
                    {/* Subtle Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-[#021f1b] via-transparent to-transparent hidden lg:block opacity-40" />
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Optional Slide Dots Indicators */}
            <div className="absolute bottom-4 left-8 md:left-12 lg:left-16 flex space-x-2 z-20">
              {SLIDES_DATA.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? "w-6 bg-emerald-400"
                      : "w-2 bg-gray-600"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
};

export default DataSecuritySection;
